import { Button } from "../../../design-system";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export type YoutubePlayerHandle = {
  play: () => void;
  pause: () => void;
  stop: () => void;
  seekTo: (timeInSeconds: number) => void;
  setMuted: (muted: boolean) => void;
};

type YoutubePlayerProps = {
  videoId: string;
  title?: string;
  playerId?: string;
  onReady?: () => void;
  onPlaybackStateChange?: (isPlaying: boolean) => void;
  onEnded?: () => void;
  onTimeDataChange?: (currentTimeSec: number, durationSec: number) => void;
  isActive?: boolean;
  showControls?: boolean;
};

const YoutubePlayer = forwardRef<YoutubePlayerHandle, YoutubePlayerProps>(
  function YoutubePlayer(
    {
      videoId,
      title = "YouTube video player",
      playerId = "playlist-youtube-player",
      onReady,
      onPlaybackStateChange,
      onEnded,
      onTimeDataChange,
      isActive = true,
      showControls = true,
    }: YoutubePlayerProps,
    ref,
  ) {
    const youtubeIframeRef = useRef<HTMLIFrameElement | null>(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    const postToYoutube = useCallback(
      (func: string, args: unknown[] = []) => {
        const iframeWindow = youtubeIframeRef.current?.contentWindow;

        if (!iframeWindow) {
          return;
        }

        iframeWindow.postMessage(
          JSON.stringify({
            event: "command",
            func,
            args,
            id: playerId,
          }),
          "*"
        );
      },
      [playerId]
    );

    const initializeYoutubeMessaging = useCallback(() => {
      const iframeWindow = youtubeIframeRef.current?.contentWindow;

      if (!iframeWindow) {
        return;
      }

      iframeWindow.postMessage(
        JSON.stringify({
          event: "listening",
          id: playerId,
          channel: "widget",
        }),
        "*"
      );

      postToYoutube("addEventListener", ["onReady"]);
      postToYoutube("addEventListener", ["onStateChange"]);
      postToYoutube("getDuration");
      postToYoutube("getCurrentTime");
    }, [playerId, postToYoutube]);

    const sendYoutubeCommand = useCallback(
      (command: "playVideo" | "pauseVideo" | "stopVideo") => {
        postToYoutube(command);
      },
      [postToYoutube],
    );

    useImperativeHandle(
      ref,
      () => ({
        play: () => sendYoutubeCommand("playVideo"),
        pause: () => sendYoutubeCommand("pauseVideo"),
        stop: () => sendYoutubeCommand("stopVideo"),
        seekTo: (timeInSeconds: number) => {
          if (!Number.isFinite(timeInSeconds) || timeInSeconds < 0) {
            return;
          }

          postToYoutube("seekTo", [timeInSeconds, true]);
        },
        setMuted: (muted: boolean) => {
          postToYoutube(muted ? "mute" : "unMute");
        },
      }),
      [postToYoutube, sendYoutubeCommand],
    );

    useEffect(() => {
      const handleYoutubeMessage = (event: MessageEvent) => {
        if (!event.origin.includes("youtube.com")) {
          return;
        }

        let payload: unknown = event.data;

        if (typeof payload === "string") {
          try {
            payload = JSON.parse(payload);
          } catch {
            return;
          }
        }

        if (typeof payload === "object" && payload !== null && "event" in payload) {
          const youtubePayload = payload as {
            event?: string;
            info?: number | { currentTime?: number; duration?: number };
            id?: string;
          };

          if (youtubePayload.id && youtubePayload.id !== playerId) {
            return;
          }

          if (youtubePayload.event === "onReady") {
            onReady?.();
            postToYoutube("getDuration");
            postToYoutube("getCurrentTime");
            return;
          }

          if (youtubePayload.info === undefined || youtubePayload.info === null) {
            return;
          }

          if (typeof youtubePayload.info === "number") {
            const infoValue = youtubePayload.info;

            if (youtubePayload.event === "onStateChange") {
              if (infoValue === 1) {
                if (isActive) {
                  onPlaybackStateChange?.(true);
                }
              } else if (infoValue === 0 || infoValue === 2 || infoValue === 5) {
                if (isActive) {
                  onPlaybackStateChange?.(false);

                  if (infoValue === 0) {
                    onEnded?.();
                  }
                }
              }
            }

            if (youtubePayload.event === "infoDelivery" && infoValue > 0) {
              setDuration((prevDuration) =>
                infoValue > prevDuration ? infoValue : prevDuration
              );
            }
            return;
          }

          if (youtubePayload.event !== "infoDelivery") {
            return;
          }

          const info = youtubePayload.info;
          const nextCurrentTime = info.currentTime;
          const nextDuration = info.duration;

          if (typeof nextCurrentTime === "number" && Number.isFinite(nextCurrentTime)) {
            setCurrentTime(nextCurrentTime);
          }

          if (typeof nextDuration === "number" && Number.isFinite(nextDuration)) {
            setDuration(nextDuration);
          }

          if (isActive) {
            onTimeDataChange?.(
              typeof nextCurrentTime === "number" && Number.isFinite(nextCurrentTime)
                ? nextCurrentTime
                : 0,
              typeof nextDuration === "number" && Number.isFinite(nextDuration)
                ? nextDuration
                : 0,
            );
          }
        }
      };

      window.addEventListener("message", handleYoutubeMessage);
      initializeYoutubeMessaging();

      const initializeTimeout = window.setTimeout(() => {
        initializeYoutubeMessaging();
      }, 600);

      const intervalId = window.setInterval(() => {
        postToYoutube("getCurrentTime");
        postToYoutube("getDuration");
      }, 500);

      return () => {
        window.removeEventListener("message", handleYoutubeMessage);
        window.clearTimeout(initializeTimeout);
        window.clearInterval(intervalId);
      };
    }, [
      initializeYoutubeMessaging,
      isActive,
      onReady,
      onEnded,
      onPlaybackStateChange,
      onTimeDataChange,
      playerId,
      postToYoutube,
    ]);

    const handleYoutubeIframeLoad = () => {
      initializeYoutubeMessaging();
    };

    const formatTime = (timeInSeconds: number) => {
      if (!Number.isFinite(timeInSeconds) || timeInSeconds <= 0) {
        return "00:00";
      }

      const totalSeconds = Math.floor(timeInSeconds);
      const minutes = Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, "0");
      const seconds = (totalSeconds % 60).toString().padStart(2, "0");

      return `${minutes}:${seconds}`;
    };

    return (
      <div>
        <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-player-border/60">
          <iframe
            id={playerId}
            ref={youtubeIframeRef}
            onLoad={handleYoutubeIframeLoad}
            className="absolute left-0 top-0 h-full w-full"
            src={`https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${encodeURIComponent(
              window.location.origin
            )}&rel=0`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
          ></iframe>
        </div>

        {showControls ? (
          <>
            <div className="mt-3 flex gap-2">
              <Button variant="subtle" size="sm"
                type="button"
                onClick={() => {
                  sendYoutubeCommand("playVideo");
                  if (isActive) {
                    onPlaybackStateChange?.(true);
                  }
                }}
              >
                Play
              </Button>
              <Button variant="subtle" size="sm"
                type="button"
                onClick={() => {
                  sendYoutubeCommand("pauseVideo");
                  if (isActive) {
                    onPlaybackStateChange?.(false);
                  }
                }}
              >
                Pause
              </Button>
              <Button variant="subtle" size="sm"
                type="button"
                onClick={() => {
                  sendYoutubeCommand("stopVideo");
                  if (isActive) {
                    onPlaybackStateChange?.(false);
                    onTimeDataChange?.(0, duration);
                  }
                }}
              >
                Stop
              </Button>
            </div>

            <p className="mt-2 text-sm text-player-secondary">
              {formatTime(currentTime)} / {formatTime(duration)}
            </p>
          </>
        ) : null}
      </div>
    );
  },
);

export default YoutubePlayer;
